/**
 * Offline-Plan P1-7 (teilweise, O1-L8): dauerhaften Speicher anfordern. Ohne ihn ist die
 * Herkunft „best effort“ – der Browser darf IndexedDB (Offline-Puffer, Stammdaten) bei
 * Speicherdruck räumen. Das Ergebnis geht in den Gerätestatus und (Paket A4) als Hinweis an
 * die Kasse.
 */
export interface SpeicherVerwaltung {
  persisted?: () => Promise<boolean>;
  persist?: () => Promise<boolean>;
}

/**
 * true = dauerhaft gewährt, false = abgelehnt, null = vom Browser nicht unterstützt oder Fehler.
 * Fragt zuerst den bestehenden Zustand ab (kein erneutes Anfordern, wenn schon gewährt).
 */
export async function fordereDauerhaftenSpeicher(
  verwaltung: SpeicherVerwaltung | undefined,
  /** Paket A4 (Codex R2): nach `persisted()` abbrechen, ohne `persist()` aufzurufen. */
  abgebrochen: () => boolean = () => false,
): Promise<boolean | null> {
  if (verwaltung?.persist === undefined) {
    return null;
  }
  try {
    if ((await verwaltung.persisted?.()) === true) {
      return true;
    }
    if (abgebrochen()) {
      return null;
    }
    return await verwaltung.persist();
  } catch {
    return null;
  }
}

/**
 * W9-A4 (OE-7): aktueller Stand beim Browser – `persisted()` jetzt abfragen, ohne anzufordern und
 * ohne zwischengespeichertes Ergebnis. Nur ein ausdrückliches `true` gilt als dauerhaft; fehlende
 * Unterstützung, Fehler oder `false` heißen „nicht dauerhaft“ (fail closed).
 */
export async function speicherJetztDauerhaft(
  verwaltung: SpeicherVerwaltung | undefined = (
    globalThis.navigator as { storage?: SpeicherVerwaltung } | undefined
  )?.storage,
): Promise<boolean> {
  try {
    return (await verwaltung?.persisted?.()) === true;
  } catch {
    return false;
  }
}

/** Ergebnis einer Anforderung: true = dauerhaft, false = abgelehnt, null = nicht unterstützt. */
export type SpeicherErgebnis = boolean | null;

/** Ereignisquelle für Bedienung und Installation (im Browser: `window`). */
export interface PersistenzEreignisse {
  addEventListener(typ: string, f: () => void): void;
  removeEventListener(typ: string, f: () => void): void;
}

/** Steuerung der laufenden Persistenz-Anforderung. */
export interface PersistenzSteuerung {
  /** Ausdrücklich erneut anfordern (Knopf, nach dem Eintragen eines Kassenschlüssels). */
  anfordern(): Promise<SpeicherErgebnis>;
  /** Ereignisse abmelden; spätere Ergebnisse werden nicht mehr gemeldet. */
  stoppe(): void;
}

/** Bedienungen, nach denen einmal je Sitzung erneut angefordert wird (Firefox fragt nur dann). */
const GESTEN = ['pointerdown', 'keydown'] as const;

/**
 * Paket A4: dauerhaften Speicher nicht nur einmal beim Start anfordern. Browser entscheiden
 * unterschiedlich – Chrome/Edge nach Nutzung bzw. installierter App, Firefox nur nach einer
 * Bedienung, Safari vor allem für installierte Apps. Deshalb:
 *
 * - beim Start,
 * - bei der ersten Bedienung dieser Sitzung (`pointerdown`/`keydown`, danach abgemeldet),
 * - nach der Installation als App (`appinstalled`),
 * - auf ausdrücklichen Aufruf von {@link PersistenzSteuerung.anfordern}.
 *
 * Anforderungen laufen nacheinander; ist der Speicher einmal dauerhaft, wird weder erneut
 * angefragt noch ein späteres „nicht dauerhaft“ gemeldet (gewährt bleibt gewährt). Nach
 * `stoppe()` wird der Browser nicht mehr gefragt, auch nicht für schon eingereihte Anfragen. Ohne
 * Browserunterstützung wird einmal `null` gemeldet und nichts beobachtet.
 */
export function starteSpeicherPersistenz(o: {
  verwaltung: SpeicherVerwaltung | undefined;
  ereignisse: PersistenzEreignisse;
  onErgebnis: (dauerhaft: SpeicherErgebnis) => void;
}): PersistenzSteuerung {
  let gewaehrt = false;
  let gestoppt = false;
  let kette: Promise<unknown> = Promise.resolve();
  const unterstuetzt = o.verwaltung?.persist !== undefined;
  const istGestoppt = (): boolean => gestoppt;

  const abmelden = (): void => {
    for (const g of GESTEN) {
      o.ereignisse.removeEventListener(g, geste);
    }
    o.ereignisse.removeEventListener('appinstalled', installiert);
  };

  const einmal = async (): Promise<SpeicherErgebnis> => {
    if (gewaehrt) {
      return true;
    }
    // Codex A4 R1: eine gestoppte Steuerung fragt den Browser nicht mehr (StrictMode, Neustart)
    if (gestoppt) {
      return null;
    }
    const d = await fordereDauerhaftenSpeicher(o.verwaltung, istGestoppt);
    if (d === true) {
      gewaehrt = true;
      abmelden();
    }
    // Während des Wartens gestoppt? (über eine Funktion gelesen, sonst verengt TypeScript)
    if (!istGestoppt()) {
      o.onErgebnis(d);
    }
    return d;
  };

  const anfordern = (): Promise<SpeicherErgebnis> => {
    if (gestoppt) {
      return Promise.resolve(gewaehrt ? true : null);
    }
    const lauf = kette.then(einmal, einmal);
    kette = lauf;
    return lauf;
  };

  function geste(): void {
    for (const g of GESTEN) {
      o.ereignisse.removeEventListener(g, geste);
    }
    void anfordern();
  }

  function installiert(): void {
    void anfordern();
  }

  if (unterstuetzt) {
    for (const g of GESTEN) {
      o.ereignisse.addEventListener(g, geste);
    }
    o.ereignisse.addEventListener('appinstalled', installiert);
  }
  void anfordern();
  return {
    anfordern,
    stoppe: () => {
      gestoppt = true;
      abmelden();
    },
  };
}

/** Kurztext der Kopfzeile, solange der Speicher nicht dauerhaft ist. */
export const SPEICHER_NICHT_DAUERHAFT = 'Speicher nicht dauerhaft';

/**
 * Braucht die Kasse den Hinweis? Nur bei einem tatsächlich gemeldeten Ergebnis (undefined =
 * Anfrage läuft noch → nichts anzeigen): abgelehnt (false) oder nicht bestätigbar (null). In
 * beiden Fällen puffert die Kasse nicht (N1.11) und der Browser darf die Gerätedaten räumen.
 */
export function speicherHinweisNoetig(dauerhaft: SpeicherErgebnis | undefined): boolean {
  return dauerhaft === false || dauerhaft === null;
}

/** Bereiche der dauerhaften Sicherung (localStorage) für die Fehlermeldung. */
export type Speicherbereich = 'bonkopf' | 'vorgang';

/** Speicherzustand für den Gerätestatus (ohne Geheimnisse). */
export interface SpeicherZustand {
  /** Ergebnis von `navigator.storage.persist()` (null = nicht unterstützt/unbekannt). */
  dauerhaft: boolean | null;
  /** Art des Offline-Puffers: IndexedDB oder nur Arbeitsspeicher (dann kein Offline-Verkauf). */
  puffer: 'indexeddb' | 'fluechtig';
  /** Codex 7: Bereiche, deren dauerhafte Sicherung zuletzt scheiterte (fehlt = keine). */
  schreibfehler?: Speicherbereich[];
}

/** Codex 7: zuletzt gescheiterte dauerhafte Sicherungen dieses Geräts (für den Gerätestatus). */
const schreibfehler = new Set<Speicherbereich>();

/** Ergebnis einer dauerhaften Sicherung festhalten (gescheitert = true). */
export function meldeSpeicherfehler(bereich: Speicherbereich, gescheitert: boolean): void {
  if (gescheitert) {
    schreibfehler.add(bereich);
  } else {
    schreibfehler.delete(bereich);
  }
}

/** Bereiche mit gescheiterter dauerhafter Sicherung (sortiert). */
export function speicherfehler(): Speicherbereich[] {
  return [...schreibfehler].sort();
}
